import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Download, 
  AlertTriangle,
  Users,
  ArrowUpCircle,
  ArrowDownCircle,
  Link2,
  History
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function IXCIntegracaoPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [previewClients, setPreviewClients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    checkStatus();
    fetchLogs();
  }, [token, navigate]);

  async function checkStatus() {
    try {
      const response = await axios.get(`${API}/api/ixc/status`, { headers });
      setStatus(response.data);
    } catch (err) {
      setStatus({ connected: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }

  async function fetchLogs() {
    try {
      const response = await axios.get(`${API}/api/ixc/sync/logs`, { headers });
      setLogs(response.data);
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
    }
  }

  async function handleSync() {
    if (!window.confirm('Deseja sincronizar os clientes do IXCsoft agora?\n\nIsso irá importar novos clientes e atualizar os existentes.')) {
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      const response = await axios.post(`${API}/api/ixc/sync`, {}, { headers });
      setSyncResult(response.data);
      fetchLogs();
    } catch (err) {
      setSyncResult({ 
        errors: [err.response?.data?.detail || err.message],
        total_found: 0,
        imported: 0,
        updated: 0,
        skipped: 0
      });
    } finally {
      setSyncing(false);
    }
  }

  async function handlePreview() {
    setShowPreview(true);
    try {
      const response = await axios.get(`${API}/api/ixc/clientes?limit=20`, { headers });
      setPreviewClients(response.data.clientes || []);
    } catch (err) {
      setPreviewClients([]);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  }

  return (
    <div className="space-y-6" data-testid="ixc-integracao-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Integração IXCsoft</h1>
          <p className="text-slate-500 text-sm mt-1">Sincronize clientes do seu sistema de gestão</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handlePreview}
            className="border-slate-300"
            data-testid="preview-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Pré-visualizar
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncing || !status?.connected}
            className="bg-green-500 hover:bg-green-600 text-white"
            data-testid="sync-btn"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Sincronizar Agora
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5" />
          Status da Conexão
        </h2>
        
        {loading ? (
          <div className="flex items-center gap-3 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            Verificando conexão...
          </div>
        ) : status?.connected ? (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-700">Conectado</p>
              <p className="text-sm text-slate-500">{status.url}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="font-medium text-red-700">Desconectado</p>
              <p className="text-sm text-slate-500">{status?.error || 'Erro de conexão'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className={`rounded-xl border p-6 ${
          syncResult.errors?.length > 0 
            ? 'bg-amber-50 border-amber-200' 
            : 'bg-green-50 border-green-200'
        }`}>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            {syncResult.errors?.length > 0 ? (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800">Sincronização Concluída com Avisos</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-green-800">Sincronização Concluída!</span>
              </>
            )}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white p-3 rounded-lg border">
              <p className="text-xs text-slate-500">Encontrados</p>
              <p className="text-xl font-bold text-slate-800">{syncResult.total_found}</p>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="flex items-center gap-1 text-xs text-green-600">
                <ArrowDownCircle className="w-3 h-3" />
                Importados
              </div>
              <p className="text-xl font-bold text-green-600">{syncResult.imported}</p>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="flex items-center gap-1 text-xs text-blue-600">
                <ArrowUpCircle className="w-3 h-3" />
                Atualizados
              </div>
              <p className="text-xl font-bold text-blue-600">{syncResult.updated}</p>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <p className="text-xs text-slate-500">Ignorados</p>
              <p className="text-xl font-bold text-slate-600">{syncResult.skipped}</p>
            </div>
          </div>

          {syncResult.errors?.length > 0 && (
            <div className="bg-white p-3 rounded-lg border border-amber-200">
              <p className="text-sm font-medium text-amber-700 mb-2">Erros:</p>
              <ul className="text-xs text-amber-600 space-y-1">
                {syncResult.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
                {syncResult.errors.length > 5 && (
                  <li>... e mais {syncResult.errors.length - 5} erros</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Sync Logs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Sincronizações
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nenhuma sincronização realizada ainda
            </div>
          ) : logs.map((log, i) => (
            <div key={i} className="p-4 hover:bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    log.result?.errors?.length > 0 ? 'bg-amber-100' : 'bg-green-100'
                  }`}>
                    {log.result?.errors?.length > 0 ? (
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-700">
                      {log.result?.imported || 0} importados, {log.result?.updated || 0} atualizados
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.timestamp)}
                    </p>
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  {log.result?.total_found || 0} encontrados
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">
                Pré-visualização de Clientes IXC
              </h2>
              <button 
                onClick={() => setShowPreview(false)} 
                className="text-slate-400 hover:text-slate-600 text-xl"
              >
                ×
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">CPF/CNPJ</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewClients.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                        Nenhum cliente encontrado ou erro ao buscar
                      </td>
                    </tr>
                  ) : previewClients.map((c, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-mono text-slate-600">{c.id}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">
                        {c.razao || c.fantasia || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{c.cnpj_cpf || c.cpf_cnpj || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{c.email || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          c.ativo === 'S' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {c.ativo === 'S' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
              <p className="text-sm text-slate-500">
                Mostrando os primeiros 20 clientes ativos
              </p>
              <Button onClick={() => setShowPreview(false)} variant="outline">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
